/** Set before silent SSO redirect; prevents retry loops when it fails. */
export const AUTH_SILENT_ATTEMPTED_KEY = 'auth_silent_attempted';

const AUTH_STORAGE_KEYS = [
  'accessToken',
  'idToken',
  'userInfo',
  'role',
  'adminInfo',
  'coordinatorInfo',
  'clientInfo',
  'evaluatorInfo',
  'auditorInfo',
] as const;

export function clearStoredAuthTokens(): void {
  for (const key of AUTH_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

export function clearAuth0Cache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('@@auth0spajs@@')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

export function clearAuthSessionFlags(): void {
  sessionStorage.removeItem(AUTH_SILENT_ATTEMPTED_KEY);
}

export function clearAuthStateForRelogin(): void {
  clearStoredAuthTokens();
  clearAuth0Cache();
  clearAuthSessionFlags();
}

const FORBIDDEN_RETURN_URL_KEY = 'forbiddenReturnUrl';

export function storeForbiddenReturnUrl(url: string): void {
  if (url && !url.includes('forbidden')) {
    sessionStorage.setItem(FORBIDDEN_RETURN_URL_KEY, url);
  }
}

export function peekForbiddenReturnUrl(fallback = '/'): string {
  const stored = sessionStorage.getItem(FORBIDDEN_RETURN_URL_KEY);
  if (stored && !stored.includes('forbidden')) {
    return stored;
  }
  return fallback;
}

export function consumeForbiddenReturnUrl(fallback = '/'): string {
  const stored = sessionStorage.getItem(FORBIDDEN_RETURN_URL_KEY);
  sessionStorage.removeItem(FORBIDDEN_RETURN_URL_KEY);
  if (stored && !stored.includes('forbidden')) {
    return stored;
  }
  return fallback;
}

export function navigateToForbidden(router: {
  url: string;
  navigate: (commands: string[]) => void;
}): void {
  storeForbiddenReturnUrl(router.url);
  router.navigate(['/forbidden']);
}

export function isJwtExpired(token: string, skewSeconds = 30): boolean {
  if (!token) {
    return true;
  }

  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    if (!payload.exp) {
      return false;
    }
    return Date.now() >= payload.exp * 1000 - skewSeconds * 1000;
  } catch {
    return true;
  }
}


/** Returns JWT payload or null if the string is not a decodable JWT. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}


const AUTH_RETURN_URL_KEY = 'authReturnUrl';

/** Path + query + hash to restore after login (skips Auth0 callback URLs). */
export function getAuthReturnUrl(): string {
  const { pathname, search, hash } = window.location;

  if (
    isAuthCallbackRoute(pathname, search) ||
    isNonRoutableAuthPath(pathname)
  ) {
    return sessionStorage.getItem(AUTH_RETURN_URL_KEY) || '/';
  }

  const returnUrl = `${pathname}${search}${hash}` || '/';
  sessionStorage.setItem(AUTH_RETURN_URL_KEY, returnUrl);
  return returnUrl;
}

export function consumeAuthReturnUrl(): string {
  const fromStorage = sessionStorage.getItem(AUTH_RETURN_URL_KEY);
  sessionStorage.removeItem(AUTH_RETURN_URL_KEY);
  return fromStorage || '/';
}

/** Auth0 redirect callback with authorization code in query string. */
export function isAuthCallbackRoute(pathname: string, search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('code') && params.has('state');
}

/** Paths that must not be stored or used as post-login return targets. */
export function isNonRoutableAuthPath(pathname: string): boolean {
  return pathname.includes('fourzerofour') || pathname.includes('forbidden');
}

export function isAuthCallbackUrl(): boolean {
  return isAuthCallbackRoute(window.location.pathname, window.location.search);
}

export function peekAuthReturnUrl(): string | null {
  return sessionStorage.getItem(AUTH_RETURN_URL_KEY);
}

/** Remove Auth0 ?code=&state= from the address bar after callback (keeps path/hash). */
export function stripAuthCallbackQueryParams(targetPath?: string): void {
  const path = targetPath || window.location.pathname || '/';
  const hash = window.location.hash || '';
  window.history.replaceState(null, '', `${path}${hash}`);
}
